import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import formatInTimeZone from 'date-fns-tz/esm/formatInTimeZone'
import format from 'date-fns/esm/format'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useLocationContext } from '@providers/location/location_provider'
import { useSessionContext } from '@providers/session/session_context'
import { buildErrorMessage } from '@shared/error/error'
import { i18WithParams as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { ButtonActions } from 'components-ui/src/button_actions/button_actions'
import { NavBlocker } from 'components-ui/src/nav_blocker/nav_blocker'
import { isEmpty, parseInteger } from 'components-ui/src/shared/common'
import { DEFAULT_TIMEZONE, toISODate } from 'components-ui/src/shared/dates'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { SpeciesButtons } from 'components-ui/src/species_buttons/species_buttons'
import { useClientLocationPetProfile } from 'service-api/src/hooks/use_client_location_pet_profiles'
import { useClientLocationSpecies } from 'service-api/src/hooks/use_client_location_species'
import { ClientAssetService } from 'service-api/src/services/client_asset.service'
import { ClientAssetUsageService } from 'service-api/src/services/client_asset_usage.service'
import { ClientLocationPetProfileService } from 'service-api/src/services/client_location_pet_profile.service'
import { captureException } from 'service-api/src/shared'
import {
  AssetUsageUpdateInput,
  LocationClientDto,
  LocationPetProfileClientDto,
  LocationPetProfileCreateInput,
  LocationPetProfileUpdateInput,
  LocationSpeciesClientDto,
} from 'service-api/src/types/api'
import { PetInstructionsForm } from './form_instructions'
import { PetProfileForm } from './form_profile'
import { PetPhotoForm } from './form_profile_photo'
import { PetVaccinesRecordsForm } from './form_vaccine_records'
import { PetVaccinesForm } from './form_vaccines'
import { UserProfileError } from './helper'
import { MemorializeDialog } from './memorialize_dialog'
import {
  TAlteredOption,
  TPetProfileSchema,
  TPetSexOption,
  getAlteredFromOption,
  getSexFromOption,
  petProfileSchema,
  setupInitialValues,
} from './schema'

const PetFormWrapper = ({ children }: { children: ReactNode }) => {
  const { navigateRoute } = useRoutes()

  const btnBackHandler = () => navigateRoute('profilePet', {})

  return (
    <Box sx={{ width: '100%' }}>
      <Button variant="text" onClick={btnBackHandler} startIcon={<KeyboardArrowLeftIcon />}>
        {t('profile.pet.backLink')}
      </Button>
      <Spacer height={2} />
      {children}
    </Box>
  )
}

interface TMyPetsEditFormProps {
  lpp?: LocationPetProfileClientDto
  species: LocationSpeciesClientDto[]
  location: LocationClientDto
  onFormState: (isDirty: boolean) => void
}

const valueOrNull = (value?: string) => (isEmpty(value) ? null : value!.trim())
const headerOffset = 60

const HIDE_MEMORIALIZE_BTN = true
export const MyPetsEditForm = ({ lpp, species, location, onFormState }: TMyPetsEditFormProps) => {
  const { navigateRoute } = useRoutes()
  const sessionCtx = useSessionContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const lupId = sessionCtx.state.user?.lupId || ''
  const [vetRecords, setVetRecords] = useState<File[]>([])
  const [petPhoto, setPetPhoto] = useState<File | undefined>(undefined)
  const defaultSpecie = species[0]
  const [showMemorializeDialog, setShowMemorializeDialog] = useState(false)

  const formik = useFormik<TPetProfileSchema>({
    initialValues: setupInitialValues(location, defaultSpecie, lpp),
    validationSchema: petProfileSchema,
    onSubmit: async (values): Promise<boolean> => {
      const { breed, dob } = values

      const payload = {
        altered: getAlteredFromOption(values.altered as TAlteredOption),
        breedId: breed.id,
        clientCareInstruction: valueOrNull(values.clientCareInstruction),
        clientFeedingInstruction: valueOrNull(values.clientFeedingInstruction),
        clientGroomingInstruction: valueOrNull(values.clientGroomingInstruction),
        clientMedicationInstruction: valueOrNull(values.clientMedicationInstruction),
        color: valueOrNull(values.color),
        dateOfBirth: isEmpty(dob) ? null : toISODate(dob!),
        displayName: values.displayName,
        locationSpeciesId: values.specie.id,
        sex: getSexFromOption(values.sex as TPetSexOption),
        vetName: valueOrNull(values.vetName),
        vetPhone: valueOrNull(values.vetPhone),
        weight: parseInteger(values.weight, undefined) || null,
        locationUserProfileId: lupId,
      }

      try {
        if (lpp) {
          await ClientLocationPetProfileService.put(
            locationName,
            lupId,
            lpp.id || '',
            payload as LocationPetProfileUpdateInput
          )
        } else {
          lpp = await ClientLocationPetProfileService.post(
            locationName,
            lupId,
            payload as LocationPetProfileCreateInput
          )
        }

        if (vetRecords.length > 0) {
          const promises = vetRecords.map(async (file) =>
            ClientAssetService.create(locationName, 'USER', 'RECORD', file, [
              {
                locationPetProfileAttachmentsId: lpp!.id,
                type: 'LOCATION_PET_PROFILE_ATTACHMENT',
                subtype: 'VET_RECORD',
              },
            ])
          )

          await Promise.all(promises)
        }

        if (petPhoto) {
          const { profileImage } = lpp
          const hasNotProfileImage = !profileImage || profileImage.length === 0

          const createdAsset = await ClientAssetService.create(locationName, 'USER', 'IMAGE', petPhoto)

          if (hasNotProfileImage) {
            await ClientAssetUsageService.create(locationName, createdAsset.id!, {
              locationPetProfileProfileImageId: lpp!.id,
              type: 'LOCATION_PET_PROFILE_PROFILE_IMAGE',
            })
          } else {
            const { id: profileImageId } = profileImage[0]
            await ClientAssetUsageService.update(locationName, profileImageId!, {
              id: profileImageId!,
              assetId: createdAsset.id!,
              locationPetProfileProfileImageId: lpp!.id,
              type: 'LOCATION_PET_PROFILE_PROFILE_IMAGE',
            } as AssetUsageUpdateInput)
          }
        }
      } catch (error) {
        captureException(new UserProfileError('Error processing user profile', error as Error))
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        return false
      }

      formik.resetForm({ values })
      // Timeout to let form reset settle and not be catch by NavBlocker
      setTimeout(() => navigateRoute('profilePet', {}), 300)
      enqueueSnackbar(t('profile.update.success'), { variant: 'success' })
      setVetRecords([])
      setPetPhoto(undefined)
      return true
    },
  })

  const breeds = useMemo(() => {
    if (!lpp) {
      const specieId = formik.values.specie.id || species[0]?.id || ''
      return species.find((s) => s.id === specieId)?.species?.breeds || []
    }
    // First try matching by locationSpecies ID (same location)
    let specie = species.find((s) => s.id === lpp!.locationSpecies?.id || '')
    // If not found, the pet was created at a different location — match by the
    // system-level speciesId from the embedded species object instead
    if (!specie) {
      const systemSpeciesId = lpp!.locationSpecies?.speciesId || lpp!.locationSpecies?.species?.id
      specie = species.find((s) => s.species?.id === systemSpeciesId || s.speciesId === systemSpeciesId)
    }
    if (!specie) {
      return []
    }
    return specie.species?.breeds || []
  }, [lpp, species, formik.values])

  const isFormDirty = useMemo(
    () => formik.dirty || vetRecords.length > 0 || petPhoto != undefined,
    [formik.dirty, petPhoto, vetRecords.length]
  )

  useEffect(() => onFormState(isFormDirty), [isFormDirty, onFormState])

  const onSubmitHandler = async () => await formik.submitForm()
  const onCancelHandler = async () => {
    if (isFormDirty) {
      formik.resetForm()
      setVetRecords([])
      setPetPhoto(undefined)
      return
    }

    setTimeout(() => navigateRoute('profilePet', {}))
    return
  }
  const onSpecieHandler = (value: LocationSpeciesClientDto) => formik.setFieldValue('specie', value)
  const onVetRecordHandler = (files: File[]) => {
    setVetRecords((prevValue) => [...prevValue, ...files])
  }
  const onPetPhotoHandler = (files: File) => setPetPhoto(files)

  const onMemorializeHandler = useCallback(
    async (date: Date | null) => {
      let payload = {}
      if (date) {
        payload = {
          dateOfDeath: format(date, 'yyyy-MM-dd'),
          deathRecordedDate: formatInTimeZone(Date.now(), location.timezone || DEFAULT_TIMEZONE, 'yyyy-MM-dd'),
          status: 'INACTIVE',
        }
      } else {
        payload = { status: 'INACTIVE' }
      }

      try {
        await ClientLocationPetProfileService.put(locationName, lupId, lpp!.id!, payload)

        formik.resetForm()
        setVetRecords([])
        setPetPhoto(undefined)
        setTimeout(() => navigateRoute('profilePet', {}))
      } catch (error) {
        captureException(new UserProfileError('Error memorializing pet', error as Error))
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.timezone, locationName, lpp, lupId]
  )

  const pageAnchor = window.location.hash.slice(1)

  useEffect(() => {
    if (!isEmpty(pageAnchor)) {
      const element = document.getElementById(pageAnchor)
      if (element) {
        const elementPosition = element.getBoundingClientRect().top
        const { scrollHeight } = document.documentElement
        // if the element is below the middle of the screen, scroll to the top of the element
        const offsetPosition = elementPosition - (elementPosition > scrollHeight / 2 ? 0 : headerOffset)

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        {!lpp ? (
          <Typography variant="h5">{t('profile.pet.form.title')}</Typography>
        ) : (
          <Typography variant="h5">{lpp.displayName}</Typography>
        )}
        <Spacer height={3} />

        <Box>
          {!lpp && (
            <>
              <SpeciesButtons
                species={species}
                selected={formik.values.specie.id || defaultSpecie.id || ''}
                onSelect={onSpecieHandler}
              />
              <Spacer height={4} />
            </>
          )}

          <PetProfileForm formik={formik} lpp={lpp} breeds={breeds} />
          <PetVaccinesForm formik={formik} lpp={lpp} />
          <Spacer height={4} />
          <PetVaccinesRecordsForm lpp={lpp} onFileUpload={onVetRecordHandler} />
          <Spacer height={4} />
          <PetInstructionsForm formik={formik} />
          <Spacer height={4} />
          <PetPhotoForm lpp={lpp} onFileUpload={onPetPhotoHandler} />

          <Spacer height={4} />
          <Stack direction="row" justifyContent="space-between">
            <ButtonActions
              onAction={onSubmitHandler}
              actionEnabled={formik.dirty || vetRecords.length > 0 || petPhoto !== undefined}
              onCancel={onCancelHandler}
              invertButtons
            />
            {lpp || HIDE_MEMORIALIZE_BTN ? null : (
              <Button variant="outlined" onClick={() => setShowMemorializeDialog(true)}>
                {t('profile.pet.memorialize')}
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>
      {showMemorializeDialog && (
        <MemorializeDialog
          lpp={lpp!}
          location={location}
          onClose={() => setShowMemorializeDialog(false)}
          onConfirm={onMemorializeHandler}
        />
      )}
    </>
  )
}

export const MyPetsEdit = () => {
  const sessionCtx = useSessionContext()
  const locationData = useLocationContext()
  const lupId = sessionCtx.state.user?.lupId || ''
  const { accountName: locationName = '', lppId = '' } = useParams()
  const [isFormDirty, setIsFormDirty] = useState(false)
  const formStateHandler = useCallback((isDirty: boolean) => setIsFormDirty(isDirty), [])

  const lpp = useClientLocationPetProfile(locationName, lupId, lppId, [
    'locationSpecies.species',
    'breed',
    'vaccinations',
    'images.asset',
    'attachments.asset',
    'profileImage.asset',
  ])
  const species = useClientLocationSpecies(locationName, true)

  if (lpp.isLoading || species.isLoading || locationData.isLoading) {
    return (
      <PetFormWrapper>
        <Skeleton variant="rectangular" width="100%" height={200} />
      </PetFormWrapper>
    )
  }

  if (lpp.isSuccess && species.isSuccess && locationData.location) {
    return (
      <NavBlocker
        sx={{ px: 3, py: 2, width: 444 }}
        shouldBlock={isFormDirty}
        promptTitle={t('profile.navBlocker.title')}
        promptContent={t('profile.navBlocker.content')}
        goBackText={t('profile.navBlocker.back')}
        proceedText={t('profile.navBlocker.proceed')}
      >
        <PetFormWrapper>
          <MyPetsEditForm
            lpp={lpp.data}
            species={species.data.results || []}
            location={locationData.location}
            onFormState={formStateHandler}
          />
        </PetFormWrapper>
      </NavBlocker>
    )
  }

  return (
    <PetFormWrapper>
      <Typography>Pet Not Found</Typography>
    </PetFormWrapper>
  )
}

export const MyPetsAdd = () => {
  const locationData = useLocationContext()
  const { accountName: locationName = '' } = useParams()
  const [isFormDirty, setIsFormDirty] = useState(false)
  const formStateHandler = useCallback((isDirty: boolean) => setIsFormDirty(isDirty), [])

  const species = useClientLocationSpecies(locationName, true)

  if (species.isLoading || locationData.isLoading) {
    return (
      <PetFormWrapper>
        <Skeleton variant="rectangular" width="100%" height={200} />
      </PetFormWrapper>
    )
  }

  if (species.isSuccess && locationData.location) {
    return (
      <NavBlocker
        sx={{ px: 3, py: 2, width: 444 }}
        shouldBlock={isFormDirty}
        promptTitle={t('profile.navBlocker.title')}
        promptContent={t('profile.navBlocker.content')}
        goBackText={t('profile.navBlocker.back')}
        proceedText={t('profile.navBlocker.proceed')}
      >
        <PetFormWrapper>
          <MyPetsEditForm
            species={species.data.results || []}
            location={locationData.location}
            onFormState={formStateHandler}
          />
        </PetFormWrapper>
      </NavBlocker>
    )
  }

  return (
    <PetFormWrapper>
      <Typography>Error loading Pet Form</Typography>
    </PetFormWrapper>
  )
}
